import { useEffect } from 'react'

/** How long the ring stays up, and when the tile's first control takes focus. */
export const ANCHOR_HIGHLIGHT_DURATION_MS = 1900
export const ANCHOR_FOCUS_DELAY_MS = 420

/**
 * The attribute `SettingsTile` keys its arrival ring off. A CONTRACT with
 * `SettingsTile.style.ts` — renaming one side silently drops the highlight, and
 * nothing in the type system connects them.
 */
export const ANCHOR_HIGHLIGHT_ATTRIBUTE = 'data-anchor-highlight'

/**
 * Re-applies the ring so the animation actually restarts.
 *
 * Writing the SAME attribute value is not a style change, so the browser does
 * not re-run the keyframes — a repeat "Adjust limit" click inside the 1.9s
 * window would land silently, which is the exact case `locationKey` exists to
 * cover. Clearing it and forcing a style recalculation before setting it again
 * is what makes the restart real; the `offsetWidth` read is the reflow, not a
 * stray expression.
 */
const restartHighlight = (target: HTMLElement): void => {
  target.removeAttribute(ANCHOR_HIGHLIGHT_ATTRIBUTE)
  void target.offsetWidth
  target.setAttribute(ANCHOR_HIGHLIGHT_ATTRIBUTE, 'true')
}

type UseAnchorArrivalHighlightArgs = {
  /** `location.hash`, leading `#` included. Empty/absent disables the effect. */
  hash: string
  /**
   * React Router's `location.key`. "Adjust limit" is reachable from the card
   * hero while the user is ALREADY on Controls & Settings, and pushing the
   * identical URL leaves `hash` untouched — the key is what makes a repeat click
   * replay the treatment instead of doing nothing visible.
   */
  locationKey?: string
  /**
   * Whether the card has landed. A pasted deep link renders the loading page
   * first, so the target tile does not exist until this flips.
   */
  ready: boolean
}

/**
 * Resolves a `#fragment` deep link and plays the arrival treatment the
 * card-management design calls for (EX-40599): center the target, ring it, then
 * focus its first control.
 *
 * WHY THIS EXISTS AT ALL: `history.push` goes through `pushState`, which by spec
 * performs no fragment navigation — no scroll, no `hashchange`. React Router v5
 * adds nothing, and the app has no other scroll-to-hash handler. Without this
 * the URL updates and the page just sits at the top.
 *
 * Lives at the page because the page is the routing boundary; the tiles that
 * carry the ids are in `src/feature/` and must not read location. Purely
 * presentational — no network, no cache work — so it is not a load boundary in
 * the sense `src/CLAUDE.md` warns about.
 */
export const useAnchorArrivalHighlight = ({
  hash,
  locationKey,
  ready,
}: UseAnchorArrivalHighlightArgs): void => {
  useEffect(() => {
    if (!hash || !ready) return undefined

    const timers: number[] = []
    // Captured so cleanup can clear the ring off the element it actually lit,
    // rather than re-resolving a hash that may have changed by then.
    let highlighted: HTMLElement | undefined

    // The destination route mounts in this same commit, so the target is not in
    // the document yet — wait one frame for it.
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(hash.slice(1))
      if (!target) return
      highlighted = target

      // `center`, not the default `start`: the tile's own `scroll-margin-top`
      // clears the fixed chrome but still leaves a tall tile's controls below
      // the fold.
      //
      // The reduced-motion check has to be HERE and not only in the tile's
      // stylesheet: the media query there silences the ring keyframes, but a
      // smooth scroll is JS-driven motion that no stylesheet can reach. Same
      // shape as `KybVerificationModal`, the other scroll-to-control in the app.
      target.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')
          ?.matches
          ? 'auto'
          : 'smooth',
        block: 'center',
      })
      restartHighlight(target)

      // Focus AFTER the scroll rather than with it: focusing mid-flight makes
      // the browser re-scroll to its own idea of where the element belongs,
      // which fights `block: 'center'` — hence `preventScroll` too. A read-only
      // Spend Limit tile renders no control at all, and `?.` is what makes that
      // a no-op instead of a crash.
      timers.push(
        window.setTimeout(() => {
          target
            .querySelector<HTMLElement>('input, select, textarea, button')
            ?.focus({ preventScroll: true })
        }, ANCHOR_FOCUS_DELAY_MS),
      )
      timers.push(
        window.setTimeout(
          () => target.removeAttribute(ANCHOR_HIGHLIGHT_ATTRIBUTE),
          ANCHOR_HIGHLIGHT_DURATION_MS,
        ),
      )
    })

    return (): void => {
      cancelAnimationFrame(frame)
      timers.forEach((timer) => window.clearTimeout(timer))
      // Clear the ring too, not just the timers. The removal timer is the only
      // thing that would have taken it off, so cancelling it alone would strand
      // a permanently-lit tile — visible on a repeat click (the tile is still
      // mounted) and on any navigation away that leaves it cached.
      highlighted?.removeAttribute(ANCHOR_HIGHLIGHT_ATTRIBUTE)
    }
  }, [hash, locationKey, ready])
}
